import type { CSSObject } from '@emotion/react'
import { kebab } from 'change-case'
import { intersection, without } from 'lodash'
import { Timing } from '../Timing'
import type { TransitionProps, ValidProperties as Properties } from './types'

export { FramerMotionTransition } from './FramerMotionTransition'

/** @private Only exported for use in tests */
export const alwaysDisableMotionProperties = ['transform'] satisfies Properties

function sanitizeProperties(properties: Properties) {
  return properties.map((property) =>
    // Names prefixed with '--' are CSS variables and should not be transformed
    property.startsWith('--') ? property : kebab(property)
  ) as Properties
}

/**
 * If `sanitizedProperties` contains motion properties — either from
 * `alwaysDisabledMotionProperties` or from the `customMotionProperties`
 * argument — generate a `transitionProperty` that excludes the motion
 * properties.
 */
function withoutMotionProperties(
  sanitizedProperties: Properties,
  customMotionProperties: NonNullable<TransitionProps['motionProperties']>
) {
  const allMotionProperties = [
    ...sanitizeProperties(customMotionProperties),
    ...alwaysDisableMotionProperties,
  ] satisfies TransitionProps['motionProperties']

  if (intersection(sanitizedProperties, allMotionProperties).length === 0)
    return {}

  const withoutMotion = without(sanitizedProperties, ...allMotionProperties)
  return {
    transitionProperty:
      withoutMotion.length > 0 ? withoutMotion.join(', ') : 'none',
  }
}

/**
 * This is a simple wrapper for CSS `transition`s. It handles converting
 * `Timing` to milliseconds, and automatically applies `prefers-reduced-motion`
 * for users who need less animation.
 *
 * @note This automatically maps camel-cased property names (like
 *   `inlineSize`) to their actual names (`inline-size`).
 *
 * @example Camel-cased property name
 *   // ...
 *   ...Transition(['backgroundColor'])
 * }
 * @example CSS `@property`
 *   // ...
 *   ...Transition(['--fillColor'])
 * @example Options
 * ...Transition(['background-color', 'block-size'], {
 *   curve: 'linear',
 *   duration: Timing.Fast,
 *   motionProperties: ['block-size'],
 * })
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-reduced-motion
 */
export const Transition = (
  properties: Properties,
  {
    curve = 'ease-in-out',
    delay = 0,
    duration = Timing.Default,
    motionProperties: customMotionProperties = [],
  }: TransitionProps = {}
): CSSObject => {
  if (!Array.isArray(properties))
    throw new TypeError('"properties" must be an array of property names')

  const sanitizedProperties = sanitizeProperties(properties)

  return {
    transitionDelay: `${delay}ms`,
    transitionDuration: `${duration}ms`,
    transitionProperty: sanitizedProperties.join(', '),
    transitionTimingFunction: curve,

    '@media (prefers-reduced-motion: reduce)': {
      // Never permit `animation`
      animation: 'none',
      ...withoutMotionProperties(sanitizedProperties, customMotionProperties),
    },
  }
}
